/**
 * Auth Interceptor
 * Добавляет JWT токен и X-Cabinet-ID к исходящим HTTP запросам.
 *
 * Supports both Keycloak and Mock auth modes:
 * - Keycloak mode: Token from KeycloakService instance
 * - Mock mode: Token from localStorage
 *
 * @story Beta-Test Task #2
 * @fix Story 17.1: WebSocket Bootstrap - Fixed token retrieval for Keycloak mode
 * @story 21.9: Cabinet Switcher — добавляет X-Cabinet-ID для /api/v1/ запросов
 * @fix fix-auth-401-zombie: долго открытая вкладка — Keycloak-токен истекает,
 * onTokenExpired не успевает/не смог обновить его, дальнейшие запросы уходили
 * БЕЗ Authorization (анонимный 401 в логах сервера) при "авторизованной" шапке.
 * В Keycloak-режиме — проактивный silent refresh ДО отправки запроса, если
 * токена нет или он истекает в ближайшие 30 сек; если refresh не удался И
 * сессия РЕАЛЬНО была (не анонимный визит) — редирект на переавторизацию.
 *
 * @fix fix-auth-401-zombie r2 (P0, обе review-lane): предыдущая версия
 * применяла refresh/login-логику КО ВСЕМ запросам без URL-исключений —
 * регрессия против удалённого bearerExcludedUrls(['/assets/','/public/']).
 * Ловило: (а) ngx-translate грузит /assets/i18n/*.json при bootstrap — у
 * анонима refresh падал → login()-редирект/блок ЕЩЁ ДО того как приложение
 * успевало отрендериться (вечный "Loading Levea Repricer..."); (б) публичные
 * роуты без auth-guard (Welcome, /calculator → POST /pricing/public/...)
 * тоже блокировались/редиректились для анонимных посетителей. Теперь:
 * только защищённые /api/v1/*-запросы (кроме /public/) участвуют в
 * refresh/login; всё остальное (assets, non-api, публичные API) — passthrough
 * без вмешательства, как было ДО того как этот файл начал работать в KC-режиме.
 *
 * @fix fix-auth-401-zombie r3 (P1/P2, gpt): shared refresh/redirect state
 * вынесен в ./auth-refresh.ts — error.interceptor.ts должен переиспользовать
 * ТУ ЖЕ дедупликацию (см. комментарий там).
 */
import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { CabinetStateService } from '../services/cabinet-state.service';
import { environment } from '../../../environments/environment';
import { getSharedFreshToken, triggerLoginRedirect } from './auth-refresh';

export { _resetAuthRefreshStateForTests as _resetAuthInterceptorStateForTests } from './auth-refresh';

/**
 * Fix auth-401-zombie r2 (P0), r3 (P3-a, gpt): только запросы к нашему
 * защищённому API участвуют в Bearer-refresh/login-логике. Всё, что НЕ
 * начинается с environment.apiUrl (assets, статика, сторонние URL) —
 * passthrough. Публичные API-пути определяются СЕГМЕНТНОЙ проверкой
 * /\/public(\/|$)/ по pathname ПОСЛЕ apiUrl (а не голым substring-поиском
 * '/public/', который ложно матчил бы что-то вроде /publicity-report) —
 * применяется к pathname без query-строки. Экспортируется —
 * error.interceptor.ts применяет тот же критерий к реактивной
 * 401-обработке (та же регрессия иначе повторилась бы там же).
 */
const PUBLIC_PATH_SEGMENT = /\/public(\/|$)/;

export function isProtectedApiRequest(req: HttpRequest<unknown>): boolean {
  if (!req.url.startsWith(environment.apiUrl)) return false;
  const pathname = req.url.split('?')[0].slice(environment.apiUrl.length);
  return !PUBLIC_PATH_SEGMENT.test(pathname);
}

function buildHeaders(
  req: HttpRequest<unknown>,
  cabinetState: CabinetStateService,
  token: string | null,
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Story 21.9: Добавить X-Cabinet-ID только для /api/v1/ запросов
  if (req.url.includes('/api/v1/')) {
    const cabinetId = cabinetState.activeCabinetId();
    if (cabinetId) {
      headers['X-Cabinet-ID'] = cabinetId;
    }
  }
  return headers;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const cabinetState = inject(CabinetStateService);

  if (environment.useKeycloakAuth) {
    if (!isProtectedApiRequest(req)) {
      // assets/i18n при bootstrap, публичные API (калькулятор и т.п.),
      // сторонние URL — не наше дело, пусть идут как шли.
      return next(req);
    }

    const needsRefresh = !authService.getToken() || authService.isTokenExpiringSoon(30);
    if (needsRefresh) {
      if (!authService.hasEverAuthenticated()) {
        // Fix r2 (P0): анонимный визит на защищённый эндпоинт БЕЗ
        // предшествующей сессии — не форсируем login(), пусть решает
        // route guard (или backend честно вернёт 401/403).
        return next(req);
      }
      return from(getSharedFreshToken(authService)).pipe(
        switchMap((freshToken) => {
          if (!freshToken) {
            // Refresh token сам протух (SSO-сессия мертва) — НЕ отправляем
            // запрос анонимно молча, сразу уводим на переавторизацию.
            // login() делает редирект — EMPTY отменяет отправку ЭТОГО запроса.
            triggerLoginRedirect(authService);
            return EMPTY;
          }
          const headers = buildHeaders(req, cabinetState, freshToken);
          return next(req.clone({ setHeaders: headers }));
        }),
      );
    }
    const headers = buildHeaders(req, cabinetState, authService.getToken());
    return next(req.clone({ setHeaders: headers }));
  }

  // Mock mode: как раньше — без Keycloak-семантики истечения/refresh.
  const token = authService.getToken();
  if (token) {
    const headers = buildHeaders(req, cabinetState, token);
    return next(req.clone({ setHeaders: headers }));
  }
  return next(req);
};
